<template>

<PageDynamic :title="pageTitle">
  <section class="section section--no-banner">
    <div class="section__content bg-gray">
      <div class="container">
        <div class="row">
          <div class="col-lg-12" data-aos="fade-left" data-aos-duration="600">
            <h1 class="sr-only">Blog</h1>
            <div class="heading-hightlight display-4 text-black">Blog</div>
          </div>
        </div>
        <form class="form-wrapper" @submit.prevent>
          <div>
            <div class="form form--search">
              <div class="form-group">
                <div class="search">
                  <input class="search__input" type="text" v-model="searchQuery" placeholder="Search" />
                  <button type="submit" class="search__icon">
                    <i class="icon-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="form form--select">
              <div class="form-group d-flex align-items-center">
                <label class="me-2 text-black-3">Year:</label>
                <select id="year" class="select__input" v-model="selectedYear" @change="filterByYear">
                  <option value="all">All</option>
                  <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </form>

        <div class="card-deck">
          <Blog :posts="currentPosts" />
        </div>

        <div>
          <Pagination v-model="page" :page="currentPage" :totalPages="totalPages" :totleItems="filteredPosts.length" :itemPerPage="itemPerPage"  />
        </div>
      </div>
    </div>
  </section>

  </PageDynamic>
</template>

<script setup>

import { usePostServiceStore } from '@/stores/postService';
import { usePageContentStore } from "@/stores/pageContent";
import { useRoute } from 'vue-router';
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";

import debounce from 'lodash/debounce';

import Blog from "@/components/apps/card/Blog.vue";
import Pagination from "@/components/apps/Pagination.vue";
import PageDynamic from "@/components/pages/Dynamic.vue";

const postStore = usePostServiceStore();
const pageStore = usePageContentStore();
const route = useRoute();
const { public: { siteUrl } } = useRuntimeConfig();

const pageTitle = computed(() => pageStore.title);

const isLoading = ref(false);
const posts = computed(() => postStore.posts);
const page = ref(1);

const years = ref([]);
const searchQuery = ref('');
const debouncedSearchQuery = ref('');
const selectedYear = ref('all');
const currentPage = ref(1);
const itemPerPage = ref(12); // item per page
const filteredPosts = ref([]);

// Extract years from posts
const extractYears = () => {
  const yearSet = new Set();
  posts.value.forEach((post) => {
    const year = new Date(post.date).getFullYear();
    yearSet.add(year);
  });
  years.value = Array.from(yearSet).sort((a, b) => b - a);
};

// Watch for searchQuery changes with debounce
watch(searchQuery, debounce((value) => {
  debouncedSearchQuery.value = value;
  applyFilters();
}, 300));

const filterByYear = () => {

  const year = selectedYear.value;

  filteredPosts.value = posts.value.filter((post) => {
    const matchesYear = year === 'all' || new Date(post.date).getFullYear().toString() == year;
    return matchesYear;
  });

}

// Filter posts
const applyFilters = () => {

  const query = debouncedSearchQuery.value.toLowerCase();
  const year = selectedYear.value;

  filteredPosts.value = posts.value.filter((post) => {
    const matchesQuery = query
      ? post.title.toLowerCase().includes(query)
      : true;
    const matchesYear =
      year === 'all' || new Date(post.date).getFullYear().toString() == year;

    return matchesQuery && matchesYear;
  });
};

// Paginated posts
const currentPosts = computed(() => {
  const startIndex = (page.value - 1) * itemPerPage.value;
  const endIndex = startIndex + itemPerPage.value;
  return filteredPosts.value.slice(startIndex, endIndex);
});

await pageStore.fetchPageContent('/blogs');

const pageUrl = computed(() => `${siteUrl}${route.path}`);

useHead(() => {
  // Last crumb is always this page — index from the end so a future CMS
  // re-nesting under a parent can't silently swap in the ancestor's name.
  const crumbs = pageStore.breadcrumbs;
  const pageName = crumbs[crumbs.length - 1]?.title?.trim() || "Blog";
  const breadcrumbItems = [
    { name: "Home", url: `${siteUrl}/` },
    { name: pageName, url: pageUrl.value },
  ];

  return {
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(useBreadcrumbSchema(breadcrumbItems, pageUrl.value)),
      },
    ],
  };
});

// Fetch data on mount
onMounted(async () => {
  
  isLoading.value = true;

  await postStore.fetchBlogData();
  
  extractYears();
  applyFilters();

  isLoading.value = false;
});



</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";

.form-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 50px;
  margin: 1.875rem 0 3.938rem;

  @include media-breakpoint-down(sm) {
    gap: 25px;
    justify-content: start;
  }
}
</style>
